import { useMemo, useState } from 'react';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { helpers } from '@/client/utils/helpers';
import { useReports, useShareReport, useRevokeReport, useCreateReport, useUpdateReport, useDeleteReport } from '@/lib/sdk/reports/queries';
import { useTeamMembers } from '@/lib/sdk/team/queries';
import type { ReportFormData } from '@/client/utils/validationSchemas';
import type { Report, ReportAccess } from '@/lib/sdk/reports/types';

export function useReportsPage() {
  const { currentOrg, user, userPermission, featurePermissions } = useAuth();
  const toast = useToast();

  const { data: reports, isLoading } = useReports(currentOrg!.id);
  const { data: teamData } = useTeamMembers({ org_id: currentOrg!.id, offset: 0, limit: 1200 });
  const teamMembers = teamData?.items ?? [];

  const { mutate: shareReport, isPending: isSharing }   = useShareReport();
  const { mutate: revokeReport }                         = useRevokeReport();
  const { mutate: createReport }                         = useCreateReport();
  const { mutate: updateReport }                         = useUpdateReport();
  const { mutate: deleteReport, isPending: isDeleting }  = useDeleteReport();

  const [searchQuery, setSearchQuery]           = useState('');
  const [selectedReport, setSelectedReport]     = useState<Report | null>(null);
  const [editingReport, setEditingReport]       = useState<Report | null>(null);
  const [deletingReport, setDeletingReport]     = useState<Report | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen]   = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [viewingReport, setViewingReport]       = useState<Report | null>(null);

  const isSuperAdmin   = user?.is_super_admin === true;
  const canAddReport   = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'reports', 'add');
  const canEditReport  = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'reports', 'edit');
  const canDeleteReport = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'reports', 'delete');
  const canShare       = helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'reports', 'share');

  const filteredReports = useMemo(() => {
    if (!reports?.items) return [];
    if (!searchQuery.trim()) return reports.items;
    const query = searchQuery.toLowerCase();
    return reports.items.filter(r => r.title.toLowerCase().includes(query) || r.description?.toLowerCase().includes(query));
  }, [reports?.items, searchQuery]);

  const handleViewReport  = (report: Report) => setViewingReport(report);
  const handleAddReport   = () => { setEditingReport(null); setIsReportDialogOpen(true); };
  const handleEditReport  = (report: Report) => { setEditingReport(report); setIsReportDialogOpen(true); };
  const handleShareReport = (report: Report) => { setSelectedReport(report); setIsShareDialogOpen(true); };
  const handleDeleteReport = (report: Report) => { setDeletingReport(report); setIsDeleteDialogOpen(true); };

  const handleShareSubmit = (reportId: string, usersToAdd: string[], usersToRevoke: string[]) => {
    const toAccess = (ids: string[]): ReportAccess[] => ids.map(u => {
      const [user_id, country_code, phone_number] = u.split('_');
      return { user_id, country_code, phone_number, report_id: reportId };
    });
    if (usersToAdd.length > 0) {
      shareReport({ reportId, access: toAccess(usersToAdd) }, {
        onSuccess: () => toast.success(`Access granted to ${usersToAdd.length} user(s)`),
        onError: (e: any) => toast.error(e.message || 'Failed to grant access'),
      });
    }
    if (usersToRevoke.length > 0) {
      revokeReport({ reportId, access: toAccess(usersToRevoke) }, {
        onSuccess: () => toast.success('Access revoked successfully'),
        onError: (e: any) => toast.error(e.message || 'Failed to revoke access'),
      });
    }
    setIsShareDialogOpen(false);
    setSelectedReport(null);
  };

  const handleReportSubmit = (data: ReportFormData) => {
    if (editingReport) {
      updateReport({ reportId: editingReport.id, data }, {
        onSuccess: () => { toast.success('Report updated successfully'); setIsReportDialogOpen(false); setEditingReport(null); },
        onError: (e: any) => toast.error(e.message || 'Failed to update report'),
      });
    } else {
      createReport({ orgId: currentOrg!.id, data }, {
        onSuccess: () => { toast.success('Report created successfully'); setIsReportDialogOpen(false); },
        onError: (e: any) => toast.error(e.message || 'Failed to create report'),
      });
    }
  };

  const handleConfirmDelete = () => {
    if (!deletingReport) return;
    deleteReport(deletingReport.id, {
      onSuccess: () => { toast.success('Report deleted successfully'); setIsDeleteDialogOpen(false); setDeletingReport(null); },
      onError: (e: any) => { toast.error(e.message || 'Failed to delete report'); setIsDeleteDialogOpen(false); },
    });
  };

  return {
    orgId: currentOrg!.id,
    reports, isLoading,
    teamMembers,
    searchQuery, setSearchQuery,
    filteredReports,
    canAddReport, canEditReport, canDeleteReport, canShare,
    isSuperAdmin: canEditReport || canDeleteReport,
    viewingReport, setViewingReport,
    selectedReport,
    editingReport, setEditingReport,
    deletingReport, setDeletingReport,
    isShareDialogOpen, setIsShareDialogOpen,
    isReportDialogOpen, setIsReportDialogOpen,
    isDeleteDialogOpen, setIsDeleteDialogOpen,
    isSharing, isDeleting,
    handleViewReport, handleAddReport, handleEditReport, handleShareReport,
    handleDeleteReport, handleShareSubmit, handleReportSubmit, handleConfirmDelete,
  };
}
